import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { InlineAlert } from "src/grading/components/InlineAlert/InlineAlert";
import LoadingButton from "src/grading/components/LoadingButton/LoadingButton";
import {
  messageOf,
  refusalCode,
  refusalCopy,
} from "src/grading/utils/apiError";
import { BenchmarkSex } from "src/models";
import { useUpdateStudentMutation } from "src/store/reducers/classes/classesApiSlice";

/** The student whose scoring data is incomplete, and which part is missing. */
export interface FixMissingTarget {
  id: string;
  firstName: string;
  lastName: string;
  missingSex: boolean;
  missingAge: boolean;
}

export interface FixMissingDataDialogProps {
  /** Open while set. */
  student: FixMissingTarget | null;
  /** The class page the dialog sits on, for refreshing its roster. */
  classId?: string;
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * Fills in a missing benchmark table or age, the two values scoring needs (FR-025). Allowed on
 * rostered students too: the roster left them blank, and the school's value is kept (R7).
 */
export const FixMissingDataDialog = ({
  student,
  classId,
  onClose,
  onSaved,
}: FixMissingDataDialogProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [updateStudent, { isLoading: saving }] = useUpdateStudentMutation();
  const [sex, setSex] = useState<BenchmarkSex | "">("");
  const [age, setAge] = useState("");
  const [error, setError] = useState<string | null>(null);

  const studentId = student?.id;
  useEffect(() => {
    setSex("");
    setAge("");
    setError(null);
  }, [studentId]);

  const submit = async () => {
    if (!student) return;
    setError(null);
    const body: { benchmarkSex?: BenchmarkSex; ageOverride?: number } = {};
    if (student.missingSex && sex) body.benchmarkSex = sex;
    if (student.missingAge && age) body.ageOverride = Number(age);
    try {
      await updateStudent({ studentId: student.id, classId, body }).unwrap();
      onSaved?.();
      onClose();
    } catch (e) {
      const code = refusalCode(e);
      setError(
        code
          ? refusalCopy(t, code)
          : messageOf(e, t("classes.updateStudentError")),
      );
    }
  };

  return (
    <Dialog
      open={!!student}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby={titleId}
    >
      <DialogTitle id={titleId} component="h2">
        {student
          ? t("student.fix.title", {
              name: `${student.firstName} ${student.lastName}`,
            })
          : ""}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {student?.missingSex ? (
          <TextField
            select
            label={t("classes.benchmarkTable")}
            value={sex}
            onChange={(e) => setSex(e.target.value as BenchmarkSex | "")}
            helperText={t("classes.recordedBySchool")}
            sx={{ mt: 1 }}
          >
            <MenuItem value="Male">{t("classes.sexMale")}</MenuItem>
            <MenuItem value="Female">{t("classes.sexFemale")}</MenuItem>
          </TextField>
        ) : null}
        {student?.missingAge ? (
          <TextField
            label={t("classes.age")}
            type="number"
            value={age}
            onChange={(e) => setAge(e.target.value)}
            inputProps={{ min: 5, max: 19 }}
            helperText={t("classes.ageHelper")}
            sx={{ mt: student.missingSex ? 0 : 1 }}
          />
        ) : null}
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          {t("common.cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={saving}
          disabled={!sex && !age}
          onClick={submit}
          label={t("common.save")}
        />
      </DialogActions>
    </Dialog>
  );
};

export default FixMissingDataDialog;
